// Browser-side mirror of server/utils/richText.js.
//
// The server sanitises on save, so stored values are already clean — this exists
// so that rendering never depends on that being true. DocumentView writes these
// strings through dangerouslySetInnerHTML, and a record written by an older
// build (or edited straight in the database) must not be able to run script in
// the user's session.
//
// Unlike the server, the browser has a real HTML parser, so we let DOMParser do
// the parsing and walk the result against the same whitelist. Keep the tag and
// style lists in step with the server copy.

const ALLOWED_TAGS = {
  B: [], STRONG: [], I: [], EM: [], U: [], S: [], STRIKE: [],
  BR: [],
  P: ['style'], DIV: ['style'], SPAN: ['style'],
  UL: [], OL: [], LI: ['style'],
};

const DROP_WITH_CONTENT = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT',
  'TEMPLATE', 'SVG', 'MATH', 'TEXTAREA', 'TITLE', 'HEAD', 'LINK', 'META',
]);

const ALLOWED_STYLE_PROPS = ['color', 'background-color'];

const COLOR_RE = /^(#[0-9a-f]{3}|#[0-9a-f]{6}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\))$/i;

const NAMED_COLORS = new Set([
  'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple',
  'gray', 'grey', 'navy', 'maroon', 'teal', 'olive', 'silver', 'lime',
  'aqua', 'cyan', 'magenta', 'fuchsia', 'brown', 'pink', 'gold', 'crimson',
  'indigo', 'violet', 'tan', 'beige', 'coral', 'salmon', 'khaki', 'plum',
  'orchid', 'turquoise', 'transparent',
]);

// NUL and friends are a tag-name smuggling primitive. Declared with new RegExp so
// this source file holds no literal control bytes of its own.
const CONTROL_CHARS = new RegExp('[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F]', 'g');

function safeColor(value) {
  const v = String(value || '').trim().toLowerCase();
  return COLOR_RE.test(v) || NAMED_COLORS.has(v) ? v : '';
}

function cleanNode(node, out) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) {           // text
      out.appendChild(document.createTextNode(child.nodeValue));
      continue;
    }
    if (child.nodeType !== 1) continue;   // drop comments and everything else

    const tag = child.tagName.toUpperCase();
    if (DROP_WITH_CONTENT.has(tag)) continue;

    if (!Object.prototype.hasOwnProperty.call(ALLOWED_TAGS, tag)) {
      // Unknown wrapper (e.g. <font> from a Word paste): keep the words, lose the tag.
      cleanNode(child, out);
      continue;
    }

    // Built fresh rather than cloned, so no attribute survives by accident.
    const el = document.createElement(tag);
    if (ALLOWED_TAGS[tag].includes('style')) {
      const parts = [];
      for (const prop of ALLOWED_STYLE_PROPS) {
        const val = safeColor(child.style.getPropertyValue(prop));
        if (val) parts.push(`${prop}:${val}`);
      }
      if (parts.length) el.setAttribute('style', parts.join(';'));
    }
    cleanNode(child, el);
    out.appendChild(el);
  }
}

export function sanitize(html) {
  if (html == null) return '';
  const src = String(html).replace(CONTROL_CHARS, '');
  if (!src) return '';
  const doc = new DOMParser().parseFromString(`<body>${src}</body>`, 'text/html');
  const out = document.createElement('div');
  cleanNode(doc.body, out);
  return out.innerHTML;
}

// Matches escapeText() in server/utils/richText.js. The lookahead is what keeps
// this idempotent: a value that already contains &nbsp; or &amp; must come back
// out unchanged, or every save/render cycle adds another "amp;" to it.
//
// Using textContent -> innerHTML here instead looks equivalent but is not: the
// DOM escapes every &, so a trailing space typed into the editor (which
// contenteditable stores as &nbsp;) would render as the literal text "&nbsp;".
function escapeText(s) {
  return String(s)
    .replace(/&(?!(#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Documents created before rich text hold plain text with newlines, which must
// not be mistaken for markup. Entities count as markup too — editor output can
// contain &nbsp; without containing a single tag.
const LOOKS_LIKE_HTML = /<[a-zA-Z/!]|&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/;

export function toHtml(value) {
  const s = value == null ? '' : String(value);
  if (!s) return '';
  if (!LOOKS_LIKE_HTML.test(s)) return escapeText(s).replace(/\r?\n/g, '<br>');
  return sanitize(s);
}

// Strips formatting down to readable text — for places that store or display a
// plain string, such as the reusable Items library.
export function toPlain(value) {
  const s = value == null ? '' : String(value);
  if (!s) return '';
  // Entity-bearing values must go through the parser too, or "Item&nbsp;" would
  // be stored in the Items library with the entity spelled out.
  if (!LOOKS_LIKE_HTML.test(s)) return s;
  const doc = new DOMParser().parseFromString(`<body>${sanitize(s)}</body>`, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

// An "empty" contenteditable still yields markup like <p><br></p>.
export function hasContent(value) {
  const s = value == null ? '' : String(value);
  if (!s) return false;
  return s.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
}
